import { cn } from "@app/lib/utils";
import type { FootprintCountry } from "@modules/investor-relations/data";
import { trans } from "@mongez/localization";
import { CountryMarker } from "./CountryMarker";
import {
  GLOBE_CENTER,
  GLOBE_HEIGHT,
  GLOBE_RADIUS,
  GLOBE_WIDTH,
  useGlobeProjection,
} from "./useGlobeProjection";

type GlobeVisualProps = {
  countries: FootprintCountry[];
  salesMarkets: [number, number][];
  selectedKey: string | null;
  onSelect: (key: string) => void;
  className?: string;
};

/**
 * The globe stage: an orthographic sphere that rotates to face whichever
 * country is selected. Land, graticule, markers, sales dots and the trail all
 * live in this one SVG so they share the projection's coordinate space —
 * `useGlobeProjection` reprojects them frame by frame.
 *
 * The city lights are the exception. There are some eleven thousand of them, far
 * past what SVG will repaint at sixty frames a second, so they go onto a canvas
 * stacked over the sphere — same projection instance, same coordinate space,
 * additive blending so overlapping halos build into conurbations. `screen`
 * blending is what lets it sit on top: the layer is black everywhere it is not a
 * light, and black leaves whatever is underneath it alone.
 */
export function GlobeVisual({
  countries,
  salesMarkets,
  selectedKey,
  onSelect,
  className,
}: GlobeVisualProps) {
  const [centerX, centerY] = GLOBE_CENTER;

  const {
    graticuleRef,
    landRef,
    highlightRef,
    highlightGroupRef,
    trailRef,
    salesGroupRef,
    bloomRef,
    oceanRef,
    terminatorRef,
    limbGlowRef,
    limbRef,
    lightsCanvasRef,
    earthCanvasRef,
    registerMarker,
    isLandReady,
    isEarthReady,
    prefersReducedMotion,
  } = useGlobeProjection({ countries, salesMarkets, selectedKey });

  return (
    // `isolate` earns its place: the three layers below order themselves by
    // z-index, and without a stacking context here those numbers would not stay
    // inside the globe. `relative` alone does not make one — it takes a
    // z-index, or this — so they would compete with the rest of the section
    // instead, where the heading sits at `z-20` and the country dropdown opens
    // downwards over the globe on every screen narrower than `xl`. The sphere
    // would land on the same level as the heading, win on document order, and
    // swallow the taps meant for the menu.
    <div className={cn("relative isolate", className)}>
      {/* The surface of the planet, wrapped onto a canvas underneath everything
          else. Stretched to the same overscanned box as the lights — see
          `OVERSCAN_X` in the hook — for the same reason.

          Every layer here carries an explicit z-index, and has to. Coming first
          in the document is not enough to put this underneath the sphere: it is
          absolutely positioned and the SVG is not, and positioned elements paint
          above in-flow content whatever the order of the markup. Left to itself
          this canvas covers the whole globe — outlines, highlight, markers and
          all — which looks exactly like them having failed to render. */}
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-[25%] -left-[50%] -right-[50%] -top-[25%] z-0"
      >
        <canvas ref={earthCanvasRef} className="block h-full w-full" />
      </div>

      <svg
        viewBox={`0 0 ${GLOBE_WIDTH} ${GLOBE_HEIGHT}`}
        role="application"
        aria-label={`${trans("globalFootprint.title")} — ${trans(
          "globalFootprint.selectCountry",
        )}`}
        className="relative z-20 block h-auto w-full overflow-visible"
      >
        <defs>
          <radialGradient id="fp-ocean" cx="38%" cy="35%" r="75%">
            <stop offset="0%" stopColor="#0d2664" />
            <stop offset="35%" stopColor="#081b4d" />
            <stop offset="70%" stopColor="#04102f" />
            <stop offset="100%" stopColor="#020a1c" />
          </radialGradient>

          {/* The bloom the sphere throws into space. It is painted underneath
              the ocean, so only the part that clears the limb is ever seen.
              The falloff is deliberately long and stepped through several
              stops: a short, strong one reads as a solid ring drawn around the
              planet rather than as air thinning out into space. */}
          <radialGradient id="fp-bloom" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#4aa8f0" stopOpacity="0" />
            <stop offset="60%" stopColor="#4aa8f0" stopOpacity="0" />
            {/* Saturated all the way down. A pale tint is the intuitive choice
                for something this faint and it is the wrong one — laid at low
                opacity over a near-black sky it loses what little colour it has
                and comes back as grey haze. The blue has to be deep enough to
                survive being thinned. */}
            <stop offset="66.7%" stopColor="#2f8bff" stopOpacity="0.55" />
            <stop offset="69%" stopColor="#1a72f5" stopOpacity="0.34" />
            <stop offset="73%" stopColor="#0f5ce6" stopOpacity="0.19" />
            <stop offset="79%" stopColor="#0a4bd0" stopOpacity="0.095" />
            <stop offset="86%" stopColor="#073cb2" stopOpacity="0.045" />
            <stop offset="93%" stopColor="#052f92" stopOpacity="0.017" />
            <stop offset="100%" stopColor="#052f92" stopOpacity="0" />
          </radialGradient>

          {/* Atmosphere seen through the limb, drawn over the land so it reads
              as depth of air. It carries on inward from where the bloom leaves
              off, so the two meet across the edge without a seam. */}
          <radialGradient id="fp-limb-glow" cx="50%" cy="50%" r="50%">
            <stop offset="93%" stopColor="#2f8ee0" stopOpacity="0" />
            <stop offset="96.5%" stopColor="#3a97e6" stopOpacity="0.08" />
            <stop offset="98.6%" stopColor="#3f9dff" stopOpacity="0.28" />
            <stop offset="99.6%" stopColor="#66b6ff" stopOpacity="0.48" />
            <stop offset="100%" stopColor="#8ccbff" stopOpacity="0.44" />
          </radialGradient>

          {/* Day/night falloff. Its focus is the light direction the canvas
              lights fade from too — see `LIGHT_OFFSET` in the hook. */}
          <radialGradient id="fp-terminator" cx="30%" cy="26%" r="72%">
            <stop offset="0%" stopColor="#000714" stopOpacity="0" />
            <stop offset="55%" stopColor="#000714" stopOpacity="0.18" />
            <stop offset="100%" stopColor="#000714" stopOpacity="0.55" />
          </radialGradient>

          {/* The limb is hot where the light strikes it and dead where it does
              not, so the glow above is masked down across the same diagonal. */}
          <linearGradient id="fp-limb-fade" x1="12%" y1="0%" x2="88%" y2="100%">
            <stop offset="0%" stopColor="#fff" stopOpacity="1" />
            <stop offset="45%" stopColor="#fff" stopOpacity="0.75" />
            <stop offset="100%" stopColor="#fff" stopOpacity="0.18" />
          </linearGradient>

          {/* Bounding-box units on both the mask and the gradient, so the fade
              spans exactly the circle it masks however far the sphere zooms. */}
          <mask id="fp-limb-mask" maskContentUnits="objectBoundingBox">
            <rect
              x="0"
              y="0"
              width="1"
              height="1"
              fill="url(#fp-limb-fade)"
            />
          </mask>

          {/* The same idea for the bloom outside the sphere, but falling away
              harder. Air only glows where the sun is striking it, so a ring of
              even brightness all the way round reads as a sticker rather than as
              atmosphere — worst of all along the bottom, which is night and
              should be giving off very nearly nothing. */}
          <linearGradient id="fp-bloom-fade" x1="8%" y1="0%" x2="80%" y2="100%">
            <stop offset="0%" stopColor="#fff" stopOpacity="1" />
            <stop offset="38%" stopColor="#fff" stopOpacity="0.5" />
            <stop offset="70%" stopColor="#fff" stopOpacity="0.12" />
            <stop offset="100%" stopColor="#fff" stopOpacity="0.02" />
          </linearGradient>

          <mask id="fp-bloom-mask" maskContentUnits="objectBoundingBox">
            <rect
              x="0"
              y="0"
              width="1"
              height="1"
              fill="url(#fp-bloom-fade)"
            />
          </mask>

          {/* Marker halos. Two fixed gradients rather than one recoloured on the
              fly: the marker picks whichever it needs when the selection
              changes, which is never during a frame of animation. */}
          <radialGradient id="fp-halo-hot" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#FE4F02" stopOpacity="0.55" />
            <stop offset="45%" stopColor="#FE4F02" stopOpacity="0.22" />
            <stop offset="100%" stopColor="#FE4F02" stopOpacity="0" />
          </radialGradient>

          <radialGradient id="fp-halo-cool" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#0387D8" stopOpacity="0.45" />
            <stop offset="45%" stopColor="#38A8FF" stopOpacity="0.18" />
            <stop offset="100%" stopColor="#38A8FF" stopOpacity="0" />
          </radialGradient>

          {/* The trail fades away from the marker, so the gradient has to run
              the other way round when the panel is on the left. */}
          <linearGradient id="fp-trail">
            <stop offset="0%" stopColor="#FE4F02" />
            <stop offset="100%" stopColor="#FE4F02" stopOpacity="0" />
          </linearGradient>

          <linearGradient id="fp-trail-rtl">
            <stop offset="0%" stopColor="#FE4F02" stopOpacity="0" />
            <stop offset="100%" stopColor="#FE4F02" />
          </linearGradient>

          {/* The selected country's outline, held here so the strokes below can
              share one shape. Carries no paint of its own — a `use` only
              inherits properties the referenced element leaves unset, so
              anything set here would override every copy with the same value. */}
          <path ref={highlightRef} id="fp-highlight-shape" fill="none" />

          {/* The bloom thrown off that outline. Bounding-box units with room to
              spare on every side, so the blur is never clipped square however
              small the country — the default region is a tenth of the box and
              would cut the halo off flat. sRGB interpolation because filters
              default to linearRGB, which drains the orange to a dirty pink. */}
          <filter
            id="fp-highlight-glow"
            x="-40%"
            y="-40%"
            width="180%"
            height="180%"
            colorInterpolationFilters="sRGB"
          >
            <feGaussianBlur stdDeviation="3.5" />
          </filter>
        </defs>

        {/* Bloom first, then the ocean over the top of it. The radii here are
            the resting size — once a country is selected the projection zooms
            in and the hook resizes every one of these to match the land. */}
        <g mask="url(#fp-bloom-mask)">
          <circle
            ref={bloomRef}
            cx={centerX}
            cy={centerY}
            r={GLOBE_RADIUS * 1.5}
            fill="url(#fp-bloom)"
            className="pointer-events-none"
          />
        </g>
        {/* The sphere the section had before there was a photograph of one, and
            the one it keeps if the photograph fails to load. Once the surface is
            up this is only in the way of it, so it goes — but on a fade, and
            only on success. */}
        <circle
          ref={oceanRef}
          cx={centerX}
          cy={centerY}
          r={GLOBE_RADIUS}
          fill="url(#fp-ocean)"
          className={cn(
            "transition-opacity duration-700",
            isEarthReady && "opacity-0",
          )}
        />

        <path
          ref={graticuleRef}
          fill="none"
          stroke="rgba(120,150,220,0.10)"
          strokeWidth={0.5}
        />

        {/* Country outlines arrive after first paint — fade them in rather than
            having the landmass pop onto a bare sphere. The fill goes the same
            way the ocean does: a flat wash of blue over the continents is the
            one thing that would hide the surface underneath it, so what is left
            once the photograph is up is the coastline alone — which the land
            still wants, both to read as drawn and to give the selected
            country's glow an edge to sit against. */}
        <path
          ref={landRef}
          fill="#12234f"
          stroke="#20386e"
          strokeWidth={0.4}
          className={cn(
            "transition-[fill,opacity] duration-700",
            isLandReady ? "opacity-100" : "opacity-0",
            isEarthReady && "fill-transparent",
          )}
        />

        {/* Shading over land and sea alike, so the sphere turns away from the
            light rather than reading as a flat disc. */}
        <circle
          ref={terminatorRef}
          cx={centerX}
          cy={centerY}
          r={GLOBE_RADIUS}
          fill="url(#fp-terminator)"
          className="pointer-events-none"
        />

        {/* The selected country, picked back out of the land underneath it — as
            a glowing border and nothing else. Filling it would be the obvious
            way to show a selection and the wrong one here: a wash of orange over
            the country hides the very city lights that make it worth looking at.

            Two wide strokes go through a blur to make the halo, and a narrow one
            stays out of it to keep the edge itself sharp. Stacking bare strokes
            was the way this worked before, and it steps rather than fades — the
            eye picks out each width as a band. The filter does cost a
            re-rasterisation on every frame of the rotation, since this shape's
            `d` is rewritten each time, but over one country's bounding box; the
            land path above re-rasterises all of Natural Earth on those same
            frames without trouble.

            The hook drives the opacity of the group through this transition so
            the colour cross-fades between countries instead of jumping.
            `duration-500` has to stay in step with `HIGHLIGHT_FADE`. */}
        <g
          ref={highlightGroupRef}
          opacity={0}
          className="pointer-events-none transition-opacity duration-500"
        >
          <g filter="url(#fp-highlight-glow)">
            <use
              href="#fp-highlight-shape"
              stroke="#FE4F02"
              strokeWidth={9}
              strokeOpacity={0.5}
              strokeLinejoin="round"
            />
            <use
              href="#fp-highlight-shape"
              stroke="#FF7A2E"
              strokeWidth={4}
              strokeOpacity={0.75}
              strokeLinejoin="round"
            />
          </g>

          <use
            href="#fp-highlight-shape"
            stroke="#FFB870"
            strokeWidth={1.6}
            strokeLinejoin="round"
          />
        </g>

        <g mask="url(#fp-limb-mask)">
          <circle
            ref={limbGlowRef}
            cx={centerX}
            cy={centerY}
            r={GLOBE_RADIUS}
            fill="url(#fp-limb-glow)"
            className="pointer-events-none"
          />
        </g>

        <circle
          ref={limbRef}
          cx={centerX}
          cy={centerY}
          r={GLOBE_RADIUS}
          fill="none"
          stroke="rgba(150,200,255,0.16)"
          strokeWidth={0.8}
          className="pointer-events-none"
        />

        <path
          ref={trailRef}
          fill="none"
          stroke="url(#fp-trail)"
          strokeWidth={1.4}
          strokeDasharray="6 6"
          opacity={0}
          className="transition-opacity duration-300"
        />

        {/* Indicative sales coverage — decorative, so kept out of the a11y tree */}
        <g ref={salesGroupRef} aria-hidden>
          {salesMarkets.map(([lon, lat]) => (
            <circle
              key={`${lon},${lat}`}
              r={2.6}
              className="pointer-events-none fill-water-blue opacity-55"
            />
          ))}
        </g>

        <g>
          {countries.map((country, index) => (
            <CountryMarker
              key={country.key}
              country={country}
              index={index}
              isActive={country.key === selectedKey}
              hasSelection={selectedKey !== null}
              prefersReducedMotion={prefersReducedMotion}
              onSelect={() => onSelect(country.key)}
              register={registerMarker}
            />
          ))}
        </g>
      </svg>

      {/* Stretched well past the stage on purpose — see `OVERSCAN_X` in the
          hook. The SVG can draw outside its viewBox as the sphere zooms; a
          canvas cannot draw outside its own element, so it is given the room up
          front. The insets go on a wrapper rather than the canvas itself: a
          canvas is a replaced element, so left/right alone would leave it at its
          intrinsic 300×150 instead of stretching between them. */}
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-[25%] -left-[50%] -right-[50%] -top-[25%] z-30"
      >
        <canvas
          ref={lightsCanvasRef}
          className="block h-full w-full mix-blend-screen"
        />
      </div>
    </div>
  );
}
